import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMe } from '@/hooks/useMe';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useAutoActivateOrg } from '@/hooks/useAutoActivateOrg';
import { ApiError } from '@/api/client';
import { ClaimPending } from '@/pages/ClaimPending';

interface RoleSwitchProps {
  expect: 'admin' | 'outreacher';
  children: ReactNode;
}

/**
 * Resolves the caller's role via `/portal/me` and:
 *   - shows children if the role matches `expect`
 *   - redirects to the correct landing if the role mismatches
 *   - shows ClaimPending on 403 OUTREACH_INVITER_NOT_FOUND
 *
 * Admins impersonating an outreacher (via ViewSwitcher) are allowed through to
 * the outreacher surface — the backend returns outreacher-shaped data when the
 * `?as=<inviterId>` param is present.
 */
export function RoleSwitch({ expect, children }: RoleSwitchProps): JSX.Element {
  useAutoActivateOrg();
  const me = useMe();
  const isAdmin = useIsAdmin();
  const { inviterId, stopImpersonating } = useImpersonation();
  const queryClient = useQueryClient();

  // Impersonation survives reloads (sessionStorage), so an admin can open an
  // admin page with it still on, e.g. by typing the URL. Arriving on the admin
  // surface that way ends it, the same as the "Admin view" button, including
  // the broad invalidation that keeps the two identities' caches apart. Only
  // checked on mount: while the outreacher picker hands over to /portal this
  // component is briefly rendered with an id set, and that must not cancel it.
  const [leavingImpersonation] = useState(() => expect === 'admin' && inviterId !== null);
  useEffect(() => {
    if (!leavingImpersonation) return;
    stopImpersonating();
    void queryClient.invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (me.isLoading || (leavingImpersonation && inviterId !== null)) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  if (me.error instanceof ApiError && me.error.code === 'OUTREACH_INVITER_NOT_FOUND') {
    return <ClaimPending />;
  }
  if (me.error) {
    return <p className="p-8 text-sm text-destructive">{(me.error as Error).message}</p>;
  }
  if (!me.data) return <></>;

  if (me.data.role !== expect) {
    // Admin impersonating an outreacher — the /me response will flip to
    // 'outreacher' once the query refreshes with the ?as= param. During the
    // brief transition where role is still 'admin', allow through if the admin
    // has selected an outreacher to impersonate.
    if (isAdmin && expect === 'outreacher' && inviterId) {
      // Allow — waiting for the impersonated /me query to resolve
    } else {
      return <Navigate to={me.data.role === 'admin' ? '/admin' : '/'} replace />;
    }
  }

  return <>{children}</>;
}
